import {
  getSavedPreviousSession,
  savePreviousSession
} from "../../utils/ragchatStorage";
import { ChatActions, ChatGetState, ChatMessage, ChatSetState, ChatState } from "../chatTypes";

type UIActions = Pick<
  ChatActions,
  | "setInputValue"
  | "setEditingMessage"
  | "setDrawerOpen"
  | "setLoading"
  | "saveAndRefreshSession"
  | "showPreviousSession"
  | "hidePreviousSession"
  | "resetChat"
>;

const PERCENT_MULTIPLIER = 100;

const buildPreviousSessionContent = (msg: ChatMessage, maxInputTokens: number): string => {
  const { artifactCount, totalTokens } = msg.contextFilter!;
  const percentage = Math.round((totalTokens / maxInputTokens) * PERCENT_MULTIPLIER);
  const usage = `${artifactCount} artifacts (${totalTokens.toLocaleString()} tokens, ${percentage}% of limit)`;
  return `Read-only — this session's context is no longer active. Previous session context was ${usage}.`;
};

const transformToPreviousSessionMessages = (messages: ChatMessage[], maxInputTokens: number): ChatMessage[] =>
  messages.map((msg) => {
    if (msg.role !== "system" || !msg.contextFilter) {
      return msg;
    }
    return { ...msg, content: buildPreviousSessionContent(msg, maxInputTokens) };
  });

const buildResetChatState = (): Partial<ChatState> => ({
  messages: [],
  messageHistory: [],
  inputValue: "",
  editingMessage: null,
  contextData: null,
  isContextLoaded: false,
  lastSelectedContext: { interactionIds: [], filter: null },
  totalTokens: 0,
  pendingQuery: null,
  isExecuting: false,
  loading: false,
  exceedsTokenLimit: false,
  isBlockedByTokenLimit: false,
  isChunkingEnabled: false,
  viewingPreviousSession: false,
  lastInjectedAnswerMode: null
});

export const createUIActions = (set: ChatSetState, get: ChatGetState): UIActions => ({
  setInputValue: (value: string): void => {
    set({ inputValue: value });
  },

  setEditingMessage: (messageId: string | null): void => {
    set({ editingMessage: messageId });
  },

  setDrawerOpen: (open: boolean): void => {
    set({ isDrawerOpen: open });
  },

  setLoading: (loading: boolean): void => {
    set({ loading });
  },

  saveAndRefreshSession: (): void => {
    const { messages } = get();
    const saved = savePreviousSession(messages);
    get().resetChat();
    set({ hasSavedPreviousSession: saved });
  },

  showPreviousSession: (): void => {
    const savedMessages = getSavedPreviousSession();
    if (!savedMessages) {
      return;
    }
    const { maxInputTokens } = get();
    const transformed = transformToPreviousSessionMessages(savedMessages, maxInputTokens);
    set({ previousSessionMessages: transformed, viewingPreviousSession: true });
  },

  hidePreviousSession: (): void => {
    set({ viewingPreviousSession: false });
  },

  resetChat: (): void => {
    set(buildResetChatState());
  }
});
